import type { Transcript } from '../../../Transcript';
import type { TextAlignment } from '../../../../enums';
import type { TextStyleRenderer } from '../../TextAsset/renderer/TextStyleRenderer';
import { CanvasEvenlyLayoutWordMetrics } from '../../TextAsset/metrics/EvenlyLayoutWordMetrics';
import { generateWordMetricsWhichFitWithinMaxWidth } from '../../TextAsset/metrics/TextMetrics';
import { SENTENCE_END_PUNCTUATIONS } from '../../TextAsset/metrics/WordMetrics';

/**
 * The minimum duration in milliseconds to be considered a pause.
 */
const MIN_PAUSE_DURATION = 2000;

/**
 * Matches a word that closes a sentence. Anchored at the end, so a full stop within a word — as in
 * a domain name like "Black.ai" — is not a sentence end.
 */
const SENTENCE_END = new RegExp(`[${SENTENCE_END_PUNCTUATIONS}]$`);

type TimeRange = { startTime: number; endTime: number };

/** Longest truncation we treat as an abbreviation rather than a word, excluding its full stop. */
const MAX_ABBREVIATION_LENGTH = 4;

/** A trailing ellipsis marks a speaker trailing off, so it closes a sentence rather than truncating a word. */
const ENDS_WITH_ELLIPSIS = /(\.{2,}|…)$/;

/** The full stop and the ellipsis are the only sentence enders an abbreviation can imitate. */
const AMBIGUOUS_SENTENCE_END = /[.…]$/;

/**
 * Whether a word ending in a full stop is an abbreviation rather than the end of a sentence.
 *
 * An abbreviation is a truncation, so it is short, and it is capitalised whenever what it stands
 * for is ("Dr.", "Prof.", "Inc."); some also carry dots inside them ("U.S.", "e.g.", "z.B."). A
 * word that genuinely ends a sentence is usually either longer ("Strava.") or lowercase ("it."),
 * so neither test alone would do — only their combination separates the two on real transcripts.
 */
const looksLikeAbbreviation = (word: string) => {
    if (ENDS_WITH_ELLIPSIS.test(word)) {
        return false;
    }
    const withoutFullStop = word.slice(0, -1);
    const initial = withoutFullStop.charAt(0);
    const isCapitalisedAndShort =
        withoutFullStop.length <= MAX_ABBREVIATION_LENGTH && initial !== initial.toLowerCase();
    return isCapitalisedAndShort || withoutFullStop.includes('.');
};

/**
 * Whether a word can open a sentence. Digits cannot: transcription tends to write "Zone. 2" where
 * the speaker said "Zone 2". Scripts without letter case always can, having no capital to offer.
 */
const opensSentence = (word: string) => {
    const initial = word.trim().charAt(0);
    if (!/\p{L}/u.test(initial)) {
        return false;
    }
    const isCaseless = initial.toLowerCase() === initial.toUpperCase();
    return isCaseless || initial === initial.toUpperCase();
};

/**
 * Whether one sentence ends at `word` and the next begins at `nextWord`.
 *
 * Only the full stop is ambiguous: no abbreviation ends in "!", "?" or their CJK counterparts, so
 * those close a sentence on their own. A full stop has to clear both an abbreviation on its left
 * and something that can open a sentence on its right.
 */
const isSentenceBoundary = (word: string, nextWord: string) => {
    const trimmed = word.trim();
    if (!SENTENCE_END.test(trimmed)) {
        return false;
    }
    if (!AMBIGUOUS_SENTENCE_END.test(trimmed)) {
        return true;
    }
    return !looksLikeAbbreviation(trimmed) && opensSentence(nextWord);
};

/**
 * Splits a time range so that each part ends on a sentence.
 *
 * Text is laid out per range, so a range boundary is also a chunk boundary. Ending a range at every
 * sentence keeps the tail of one sentence out of the chunk that starts the next.
 */
const splitRangeAtSentenceEnds = (transcript: Transcript, range: TimeRange): TimeRange[] => {
    // Words blanked out by a text correction are dropped from the rendered text, so pair each word
    // with the next one that is actually shown, or a blank between two sentences hides the boundary.
    const words = transcript
        .getWordsWithinRange([[range.startTime, range.endTime]])
        .filter(({ word }) => word.trim() !== '');
    const ranges: TimeRange[] = [];
    let startTime = range.startTime;

    words.slice(0, -1).forEach((word, index) => {
        const nextWord = words[index + 1];
        if (!isSentenceBoundary(word.word, nextWord.word)) {
            return;
        }
        ranges.push({ startTime, endTime: word.endTime });
        startTime = nextWord.startTime;
    });

    ranges.push({ startTime, endTime: range.endTime });
    return ranges;
};

export const buildMetricsForFancyCaptions = ({
    transcript,
    alignment,
    style,
    maxWidth,
    maxHeight,
}: {
    transcript: Transcript;
    alignment: TextAlignment;
    style: TextStyleRenderer;
    maxWidth: number;
    maxHeight: number;
}) => {
    let wordIndexOffset = 0;
    const timeRanges = transcript
        .getTimeRangesWithoutPauses(MIN_PAUSE_DURATION)
        .flatMap((timeRange) => splitRangeAtSentenceEnds(transcript, timeRange));

    return timeRanges.flatMap((timeRange) => {
        const tempTranscript = transcript.getTrimmedTranscript([
            [timeRange.startTime, timeRange.endTime],
        ]);
        const { wordsWithSpaces, sourceWordReferenceIndices } =
            tempTranscript.generateWordsFromTranscript(wordIndexOffset);

        // get the last word index (-1 are spaces). A range can hold no words at all when every word
        // in it was blanked out by a text correction.
        const lastWordIndex = sourceWordReferenceIndices.filter((num) => num !== -1).pop();
        wordIndexOffset = lastWordIndex === undefined ? wordIndexOffset : lastWordIndex + 1;

        const wordMetricsList = generateWordMetricsWhichFitWithinMaxWidth({
            words: wordsWithSpaces,
            highlightAreas: [],
            sourceWordReferenceIndices,
            normalStyle: style,
            highlightStyle: style,
            maxWidth,
            wordBreak: true,
        });

        return CanvasEvenlyLayoutWordMetrics.layout({
            wordMetricsList,
            alignment,
            style,
            wordBreak: true,
            maxWidth,
            maxHeight,
            removeTrailingPunctuationOnLastLineFromLastChunk: true,
        });
    });
};
